import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { translate } from '@jsverse/transloco';
import { map, startWith } from 'rxjs';

import {
  ActivityBarPillDataSourceTypeEnum,
  ActivityBarPillTypeEnum,
} from '../dashboard-pill/dashboard-pill.component';
import { AppSharedModule } from '../../app-shared.module';
import { WidgetDataSource } from '../../models';
import {
  DashboardValidityChecks,
  ValidityResult,
} from '../../modules/dashboard/services/validity-checks';
import { DatasourcesType } from '../../modules/dashboard/stores/datasource-manager.store';
import { CustomFieldsService } from '../../modules/field/custom-fields.service';
import {
  DatasourceManager,
  AutocompleteState,
  DataSourceItem,
} from '../../modules/widget/components/data-source-chooser/data-source-chooser.component';
import { WidgetComponent } from '../../modules/widget/components/widget/widget.component';
import { AbstractChartReport } from '../../modules/widget/services/chart-report/chart-report-abstract/chart-report-abstract';
import {
  Comparison,
  Dashboard,
  DASHBOARD_LEVEL_WIDGET_GROUP_ID,
  Widget,
  WidgetGroup,
} from '../../providers/bridge-api/dashboard.service';
import { Field } from '../../providers/storage-api/field.service';

@Component({
  selector: 'app-dashboard-pill-entity-selector',
  templateUrl: './dashboard-pill-entity-selector.component.html',
  styleUrls: ['./dashboard-pill-entity-selector.component.scss'],
  imports: [AppSharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardPillEntitySelectorComponent implements OnInit, OnChanges, AfterViewInit {
  @ViewChild('elementInput') elementInput: ElementRef;
  readonly ActivityBarPillTypeEnum = ActivityBarPillTypeEnum;
  private dataSourceManager: DatasourceManager;

  @Input() mode: ActivityBarPillTypeEnum;
  @Input() dataSourceType: ActivityBarPillDataSourceTypeEnum;
  @Input() value: WidgetDataSource | Comparison | WidgetGroup;
  @Input() dataSources: DatasourcesType;
  @Input() entityList: Array<Comparison | WidgetGroup>;
  @Input() valuePlaceholder = translate('widgets.-none-');
  @Input() widget?: Widget;
  @Input() report?: AbstractChartReport;
  @Input() dashboard?: Dashboard;
  @Input() accountMetadata?: Field[];
  @Input() widgetComponent?: WidgetComponent;
  @Output() cancel = new EventEmitter<void>();
  @Output() entitySelected = new EventEmitter<WidgetDataSource | Comparison | WidgetGroup>();

  public selectedEntity = '';
  public inputAc: AutocompleteState = {
    timeout: null,
    inProgress: false,
    results: [],
    control: new UntypedFormControl(),
  };

  entityProps: { [id: string]: ValidityResult } = {};
  public dataSourceItems: DataSourceItem[] = [];

  constructor(private customFieldsService: CustomFieldsService) {}

  ngOnInit(): void {
    this.dataSourceManager = new DatasourceManager(this.dataSources, this.valuePlaceholder);
    this.searchEntityHandlerByTerm(null);
    this.inputAc.observable = this.inputAc.control.valueChanges.pipe(
      startWith(''),
      map((term: string) => this.searchEntityHandlerByTerm(term).filter(Boolean))
    );
    this.fillEntityProps();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.widget && changes.widget.previousValue !== this.widget) ||
      (changes.report && changes.report.previousValue !== this.report) ||
      (changes.dashboard && changes.dashboard.previousValue !== this.dashboard)
    ) {
      this.fillEntityProps();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.elementInput.nativeElement.focus());
  }

  public widgetRefreshed() {
    this.fillEntityProps();
  }

  private searchEntityHandlerByTerm(term: string): Array<any> {
    switch (this.mode) {
      case ActivityBarPillTypeEnum.DATA_SOURCE:
        return (this.dataSourceItems = this.dataSourceManager.search(null, term).filter((ds) => {
          if (this.dataSourceType === ActivityBarPillDataSourceTypeEnum.KEY_DRIVER) {
            return ds?.value?.isDriverAnalysis;
          }
          if (ds?.value?.isDriverAnalysis) {
            return false;
          }
          if (this.widget?.payload?.type === 'journey' && !ds?.value?.isJourneyGroup) {
            return false;
          }
          return true;
        }));
      default:
        return this.search(null, term);
    }
  }

  public iconFor(entity: WidgetDataSource | Comparison) {
    switch (this.mode) {
      case ActivityBarPillTypeEnum.DATA_SOURCE:
        if (entity && entity.type === 'group') {
          return 'folder';
        }
        return (entity as WidgetDataSource).isDriverAnalysis
          ? 'document_chart_filled'
          : 'document_chart';
      case ActivityBarPillTypeEnum.COMPARISON:
        return entity && 'search_suggestion_filled';
    }
  }

  public selectEntity(item: DataSourceItem | Comparison | WidgetGroup) {
    if (this.mode === ActivityBarPillTypeEnum.DATA_SOURCE) {
      if (!item) {
        return;
      }

      if (this.value !== (item as DataSourceItem)?.value) {
        this.value = (item as DataSourceItem).value;
      }
    }
    if (this.mode === ActivityBarPillTypeEnum.COMPARISON) {
      this.value = item as Comparison;
    }
    if (this.mode === ActivityBarPillTypeEnum.WIDGET_GROUP) {
      this.value = item as WidgetGroup;
    }
    this.entitySelected.emit(this.value);
  }

  clearSelectedEntity() {
    this.cancel.emit();
  }

  updateEntities() {
    clearTimeout(this.inputAc.timeout);
    this.inputAc.timeout = setTimeout(() => {
      this.inputAc.inProgress = true;
      this.inputAc.results = this.getInputAcResultForUpdate();
      this.inputAc.timeout = null;
      this.inputAc.inProgress = false;
    }, 400);
  }

  getInputAcResultForUpdate(): Array<any> {
    switch (this.mode) {
      case ActivityBarPillTypeEnum.DATA_SOURCE:
        return this.dataSourceManager.search(this.value as WidgetDataSource, this.selectedEntity);
      default:
        return this.search(null, this.selectedEntity);
    }
  }

  resetEntity() {
    this.selectedEntity = '';
    this.cancel.emit();
  }

  public search(
    excludeItem?: Comparison | WidgetGroup,
    term = ''
  ): Array<Comparison | WidgetGroup> {
    let returnValue = [...this.entityList];
    if (typeof term === 'string') {
      returnValue = this.entityList.filter(
        (c) => c && c.title.toLowerCase().includes(term.toLowerCase())
      );
    }
    if (excludeItem) {
      returnValue = this.entityList.filter((c) => c.id !== excludeItem.id);
    }
    if (
      !returnValue.includes(null) &&
      this.mode === ActivityBarPillTypeEnum.COMPARISON &&
      !this.widget
    ) {
      returnValue.unshift(null);
    }
    return returnValue;
  }

  private async fillEntityProps() {
    this.entityProps = {};

    if (this.widget) {
      if (this.dataSourceItems.length) {
        this.dataSourceItems.forEach(async (item) => {
          const dsCustomFields = await this.customFieldsService.getMetadataSubTypesAsync(
            item.value
          );
          item.value.customFields = dsCustomFields;
          this.entityProps[item.value.id] = DashboardValidityChecks.dataSourceValidCheck(
            this.widget,
            this.report,
            item.value,
            this.accountMetadata,
            false,
            this.dashboard
          );
          this.fillDatasourceTooltip(item);
        });
      }
      if (this.entityList?.some((e) => e['type'])) {
        this.entityList.forEach((entity: Comparison) => {
          this.entityProps[entity.id] = {
            disabled: !this.widgetComponent.canApplyComparison(entity),
            tooltip: undefined,
          };
          if (this.entityProps[entity.id].disabled) {
            this.entityProps[entity.id].tooltip = translate(
              'apply-comparison-dialog-component.not-compatible'
            );
          }
        });
      }
      if (this.mode === 'WIDGET_GROUP' && this.widgetComponent.isWidgetGroupParent) {
        this.entityList.forEach((entity: WidgetGroup) => {
          this.entityProps[entity.id] = {
            disabled: entity.id === DASHBOARD_LEVEL_WIDGET_GROUP_ID,
            tooltip:
              entity.id === DASHBOARD_LEVEL_WIDGET_GROUP_ID
                ? translate('dashboard-pill.dashboard-filters-cannot-be-parents')
                : '',
          };
        });
      }
      return;
    }

    if (this.dashboard) {
      this.dataSourceItems.forEach((item) => {
        this.entityProps[item.value.id] = DashboardValidityChecks.dataSourceValidCheckDashboard(
          this.dashboard,
          item.value
        );
        this.fillDatasourceTooltip(item);
      });
      return;
    }
  }

  private fillDatasourceTooltip(item: DataSourceItem) {
    if (!this.entityProps[item.value.id].tooltip && item.value.tags?.includes('fromWidget')) {
      this.entityProps[item.value.id].tooltip = this.dataSourceManager.displayForDataSource(
        item.value
      );
    }
  }
}
